import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = {
  default: {
    accent: "#00E5FF",
    button: "#00E5FF",
    buttonHover: "#66FFFF",
    border: "rgba(0,229,255,0.35)",
  },

  pizza: {
    accent: "#E63946",
    button: "#E63946",
    buttonHover: "#F15B67",
    border: "rgba(230,57,70,0.35)",
  },

  burger: {
    accent: "#F4B400",
    button: "#F4B400",
    buttonHover: "#FFD54F",
    border: "rgba(244,180,0,0.35)",
  },

  tacos: {
    accent: "#2E7D32",
    button: "#2E7D32",
    buttonHover: "#43A047",
    border: "rgba(46,125,50,0.35)",
  },

  cafe: {
    accent: "#6F4E37",
    button: "#6F4E37",
    buttonHover: "#8D6E63",
    border: "rgba(111,78,55,0.35)",
  },

  italian: {
    accent: "#C62828",
    button: "#C62828",
    buttonHover: "#E53935",
    border: "rgba(198,40,40,0.35)",
  },

  fine_dining: {
    accent: "#D4AF37",
    button: "#D4AF37",
    buttonHover: "#E5C85B",
    border: "rgba(212,175,55,0.35)",
  },

  bbq: {
    accent: "#B3541E",
    button: "#B3541E",
    buttonHover: "#C96A2C",
    border: "rgba(179,84,30,0.35)",
  },

  seafood: {
    accent: "#0288D1",
    button: "#0288D1",
    buttonHover: "#29B6F6",
    border: "rgba(2,136,209,0.35)",
  },

  cajun: {
    accent: "#D84315",
    button: "#D84315",
    buttonHover: "#F4511E",
    border: "rgba(216,67,21,0.35)",
  },

  chicken: {
    accent: "#E6A700",
    button: "#E6A700",
    buttonHover: "#FBC02D",
    border: "rgba(230,167,0,0.35)",
  },

  chinese: {
    accent: "#B71C1C",
    button: "#B71C1C",
    buttonHover: "#D32F2F",
    border: "rgba(183,28,28,0.35)",
  },

  sushi: {
    accent: "#5E35B1",
    button: "#5E35B1",
    buttonHover: "#7E57C2",
    border: "rgba(94,53,177,0.35)",
  },

  bakery: {
    accent: "#C68642",
    button: "#C68642",
    buttonHover: "#D9A066",
    border: "rgba(198,134,66,0.35)",
  },

  icecream: {
    accent: "#EC4899",
    button: "#EC4899",
    buttonHover: "#F472B6",
    border: "rgba(236,72,153,0.35)",
  },

  hotdog: {
    accent: "#C62828",
    button: "#C62828",
    buttonHover: "#E53935",
    border: "rgba(198,40,40,0.35)",
  },
};

function themeFor(data) {
  const key = (data?.theme || "default").toLowerCase();
  const base = THEMES[key] || THEMES.default;
  const custom = String(data?.brand_color || "").trim();

  if (!/^#[0-9A-Fa-f]{6}$/.test(custom)) {
    return base;
  }

  return {
    ...base,
    accent: custom,
    button: custom,
    buttonHover: custom,
    border: `${custom}59`,
  };
}

function automaticCustomerActions({
  websiteUrl = "",
  menuUrl = "",
  phone = "",
  address = "",
  excludeHref = "",
} = {}) {
  const normalize = (value = "") => String(value || "").trim();
  const excluded = normalize(excludeHref);

  const actions = [];

  const add = (label, href, action) => {
    const cleanHref = normalize(href);
    if (!cleanHref) return;

    if (
      excluded &&
      externalUrl(cleanHref) === externalUrl(excluded)
    ) {
      return;
    }

    if (
      actions.some(
        (item) =>
          externalUrl(item.href) === externalUrl(cleanHref)
      )
    ) {
      return;
    }

    actions.push({
      label,
      href: cleanHref,
      action,
    });
  };

  add("Website", websiteUrl, "website_click");
  add("View Menu", menuUrl, "menu_click");

  if (phone) {
    add(
      "Call",
      `tel:${String(phone).replace(/[^0-9+]/g, "")}`,
      "call_click"
    );
  }

  if (address) {
    add(
      "Directions",
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`,
      "directions_click"
    );
  }

  return actions;
}


function CustomerActionGrid({
  actions = [],
  onTrack,
  className = "mt-3",
}) {
  if (!actions.length) return null;

  const count = actions.length;
  const columns =
    count === 1
      ? "grid-cols-1"
      : count === 3
        ? "grid-cols-3"
        : "grid-cols-2";

  return (
    <div className={`${className} grid ${columns} gap-3`}>
      {actions.map((item) => {
        const href = String(item.href || "");
        const isExternal = href.startsWith("http");

        return (
          <a
            key={`${item.label}-${item.href}`}
            href={href.startsWith("tel:") ? href : externalUrl(href)}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noreferrer" : undefined}
            onClick={() => onTrack(item.action)}
            className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-center text-xs font-semibold text-white transition-colors hover:bg-white/10"
          >
            {item.label}
          </a>
        );
      })}
    </div>
  );
}


function strongRatingLabel(data = {}) {
  const rating = Number(data.rating || 0);
  const reviewCount = Number(
    data.review_count || data.rating_count || 0
  );

  if (rating < 4.3 || reviewCount < 25) return "";

  return `★ ${rating.toFixed(1)} · ${reviewCount.toLocaleString()} reviews`;
}

function readOrCreateClientId() {
  try {
    let cid = localStorage.getItem("ttt_cid");
    if (!cid) {
      cid =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("ttt_cid", cid);
    }
    return cid;
  } catch {
    return "";
  }
}

function externalUrl(url = "") {
  let cleaned = String(url || "").trim();

  cleaned = cleaned.replace("https://taptotable.co/r/", "");
  cleaned = cleaned.replace("http://taptotable.co/r/", "");
  cleaned = cleaned.replace("taptotable.co/r/", "");
  cleaned = cleaned.replace(/^\/r\//, "");
  cleaned = cleaned.replace(/^\/+/, "");

  cleaned = cleaned.replace(/^https:\/(?!\/)/, "https://");
  cleaned = cleaned.replace(/^http:\/(?!\/)/, "http://");
  cleaned = cleaned.replace(/^https\/\//, "https://");
  cleaned = cleaned.replace(/^http\/\//, "http://");
  cleaned = cleaned.replace(/^https\//, "https://");
  cleaned = cleaned.replace(/^http\//, "http://");

  if (!cleaned) return "";

  return cleaned.startsWith("http://") || cleaned.startsWith("https://")
    ? cleaned
    : `https://${cleaned}`;
}

function formatEventDate(value = "") {
  if (!value) return "";

  try {
    return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function eventActionsFor(data = {}) {
  const actions = [];

  // Always keep the selected event action first, even when its destination
  // has not been entered yet.
  actions.push({
    label: data.event_action_text || "View Event Details",
    href: data.event_action_link || "",
    action: "event_action_click",
    primary: true,
  });

  if (data.website_url) {
    actions.push({
      label: "Website",
      href: data.website_url,
      action: "website_click",
    });
  }

  if (data.phone) {
    actions.push({
      label: "Call",
      href: `tel:${String(data.phone).replace(/[^0-9+]/g, "")}`,
      action: "call_click",
    });
  }

  if (data.address) {
    actions.push({
      label: "Directions",
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        data.address
      )}`,
      action: "directions_click",
    });
  }

  return actions.slice(0, 4);
}

export default function Funnel() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimCode, setClaimCode] = useState("");
  const [claimTime, setClaimTime] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);

  const getClientId = useCallback(() => readOrCreateClientId(), []);

  useEffect(() => {
    let cancelled = false;

    async function loadFunnel() {
      try {
        const url = new URL(window.location.href);
        const hasResetFlag = url.searchParams.has("reset");
        const hasPreviewFlag = url.searchParams.has("preview");

        // Never leave internal testing flags in a customer-shareable URL.
        if (hasResetFlag || hasPreviewFlag) {
          url.searchParams.delete("reset");
          url.searchParams.delete("preview");
          window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        }

        const res = await axios.get(`${API}/funnels/${slug}`);
        if (cancelled) return;

        setData(res.data);
        setState("ok");

        axios.post(`${API}/funnels/${slug}/visit`, {
          client_id: getClientId(),
        }).catch(() => {});

        // A fresh page load always starts on Page 1.
        // If this browser claims again within 24 hours, the backend returns
        // the same reserved claim code without incrementing analytics.
        setClaimed(false);
        setClaimCode("");
        setClaimTime(null);
      } catch (err) {
        if (cancelled) return;
        setState(err?.response?.status === 404 ? "notfound" : "error");
      }
    }

    loadFunnel();

    return () => {
      cancelled = true;
    };
  }, [slug, getClientId]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 text-center text-white">
        <div className="font-display text-3xl font-semibold">This page isn’t live yet</div>
        <p className="mt-3 text-zinc-400 max-w-sm">
          This TapToTable page is not active yet. Please check back soon.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-5 py-2.5 rounded-full hover:bg-[#66FFFF] transition-colors"
        >
          Visit TapToTable
        </Link>
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 text-center text-white">
        <div className="font-display text-2xl font-semibold">Something went wrong</div>
        <p className="mt-2 text-zinc-400">Please refresh and try again.</p>
      </div>
    );
  }

  const palette = themeFor(data);
  const ratingLabel = strongRatingLabel(data);
  const isActive = data.account_status === "active" || data.payment_status === "paid";
  const showActivation = data.payment_link && !isActive;
  const isEvent = (data.campaign_type || "offer") === "event";
  const isFeaturedItem = data.campaign_type === "featured_item";
  const eventDateLabel = formatEventDate(data.event_date);
  const eventActions = eventActionsFor(data);

  const trackClick = () => {
    // General customer-action clicks stay separate from offer claims
    // and event-action analytics.
  };

  const trackEventAction = (action = "event_primary") => {
    if (!isEvent) return;

    const url = `${API}/funnels/${slug}/event-action`;
    const payload = JSON.stringify({
      client_id: getClientId(),
      action,
    });

    try {
      if (navigator.sendBeacon) {
        const body = new Blob([payload], {
          type: "text/plain;charset=UTF-8",
        });
        const queued = navigator.sendBeacon(url, body);
        if (queued) return;
      }
    } catch {}

    try {
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=UTF-8",
        },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Analytics must never block the customer's event action.
    }
  };

  const onClaim = async () => {
if (isEvent || isFeaturedItem || claiming || claimed) return;

    setClaiming(true);

    try {
      const response = await axios.post(
        `${API}/funnels/${slug}/claim`,
        {
          client_id: getClientId(),
        }
      );

      const issuedCode = String(
        response?.data?.claim_code || ""
      ).trim();

      if (!issuedCode) {
        throw new Error(
          "Claim code was not returned by the server."
        );
      }

      const issuedAt = response?.data?.claimed_at
        ? new Date(response.data.claimed_at)
        : new Date();

      setClaimCode(issuedCode);
      setClaimTime(
        Number.isNaN(issuedAt.getTime())
          ? new Date()
          : issuedAt
      );
      setClaimed(true);
    } catch (err) {
      console.error("Claim failed:", err);
      toast.error(
        "Could not claim this offer. Please try again."
      );
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white flex flex-col"
      style={{
        background: `radial-gradient(ellipse at 20% 0%, ${palette.via} 0%, ${palette.from} 45%, #050507 100%)`,
      }}
    >
      <main className="flex-1 flex flex-col items-center px-6 pt-10 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 opacity-70 hover:opacity-100 transition">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[#00E5FF] text-black font-display font-bold text-[10px]">
                T
              </span>
              <span className="font-display text-xs">TapToTable</span>
            </Link>

            {data.instagram_handle ? (
              <a
                href={`https://instagram.com/${data.instagram_handle}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-zinc-400 hover:text-white transition"
              >
                @{data.instagram_handle}
              </a>
            ) : null}
          </div>

          {showActivation ? (
            <div className="mt-6 rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF]">
                Restaurant Owner Activation
              </div>
              <div className="mt-2 font-display font-semibold text-lg">
                {isEvent
                  ? "Activate this TapToTable event page"
                  : "Activate this TapToTable page"}
              </div>
              <p className="mt-1 text-xs text-zinc-400">
                $59/month. Everything included. Cancel anytime.
              </p>
              <a
                href={data.payment_link}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackClick("payment_click")}
                className="mt-4 inline-flex w-full justify-center rounded-2xl bg-[#00E5FF] px-4 py-3 font-semibold text-black hover:bg-[#66FFFF] transition-colors"
              >
                Activate Page
              </a>
            </div>
          ) : null}

          {isEvent ? (
            <motion.div
              key="event-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl shadow-2xl"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                {data.image_url && !imageFailed ? (
                  <img
                    src={data.image_url}
                    alt={data.restaurant_name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <GradientArt palette={palette} />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

                <div className="absolute bottom-4 left-5 right-5">
                  <div
                    className="text-[10px] uppercase tracking-[0.2em] font-bold"
                    style={{ color: palette.accent }}
                  >
                    Upcoming Event
                  </div>

                  {ratingLabel ? (
                    <div
                      className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: palette.accent }}
                    >
                      {ratingLabel}
                    </div>
                  ) : null}

                  <h1 className="font-display font-semibold text-2xl sm:text-3xl leading-tight mt-1">
                    {data.restaurant_name}
                  </h1>

                  {data.instagram_handle ? (
                    <div className="mt-1 text-xs text-zinc-300">
                      @{data.instagram_handle}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="p-6">
                <div
                  className="text-[10px] uppercase tracking-[0.2em] font-bold"
                  style={{ color: palette.accent }}
                >
                  Event
                </div>

                <div className="mt-2 font-display font-semibold text-xl leading-snug">
                  {data.event_name || "Restaurant Event"}
                </div>

                {(eventDateLabel || data.event_time) ? (
                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-zinc-300">
                    {eventDateLabel ? (
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                        {eventDateLabel}
                      </span>
                    ) : null}

                    {data.event_time ? (
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                        {data.event_time}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {data.event_description ? (
                  <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                    {data.event_description}
                  </p>
                ) : null}

                {eventActions.length > 0 ? (
                  <div className="mt-6 space-y-3">
                    {eventActions[0] ? (
                      eventActions[0].href ? (
                        <a
                          href={externalUrl(eventActions[0].href)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() =>
                            trackEventAction(
                              eventActions[0].action || "event_primary"
                            )
                          }
                          className="inline-flex min-h-[54px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-base font-semibold transition-opacity hover:opacity-90"
                          style={{
                            background: palette.accent,
                            color: "#000",
                          }}
                        >
                          {eventActions[0].label}
                        </a>
                      ) : (
                        <div
                          className="inline-flex min-h-[54px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-base font-semibold opacity-70"
                          style={{
                            background: palette.accent,
                            color: "#000",
                          }}
                          title="This event button does not have a destination link yet."
                        >
                          {eventActions[0].label}
                        </div>
                      )
                    ) : null}

                    {eventActions.slice(1).length > 0 ? (
                      <div className="grid grid-cols-3 gap-3">
                        {eventActions.slice(1).map((action) => (
                          <a
                            key={`${action.action}-${action.label}`}
                            href={
                              action.href.startsWith("tel:")
                                ? action.href
                                : externalUrl(action.href)
                            }
                            target={
                              action.href.startsWith("http")
                                ? "_blank"
                                : undefined
                            }
                            rel={
                              action.href.startsWith("http")
                                ? "noreferrer"
                                : undefined
                            }
                            onClick={() => trackClick(action.action)}
                            className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-white/10 bg-white/5 px-2 py-2.5 text-center text-xs font-semibold transition-colors hover:bg-white/10"
                          >
                            {action.label}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {(data.address || data.phone) ? (
                  <div className="mt-5 space-y-1 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-300">
                    {data.address ? <div>{data.address}</div> : null}
                    {data.phone ? (
                      <a
                        href={`tel:${String(data.phone).replace(/[^0-9+]/g, "")}`}
                        onClick={() => trackClick("call_click")}
                        className="block text-zinc-400 transition-colors hover:text-white"
                      >
                        {data.phone}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </motion.div>

) : isFeaturedItem ? (
<motion.div
  key="featured-item-card"
  initial={{ opacity: 0, y: 24 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6 }}
  className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl shadow-2xl"
>
  <div className="relative aspect-[4/3] w-full overflow-hidden">
    {data.image_url && !imageFailed ? (
      <img
        src={data.image_url}
        alt={data.featured_item_name || data.restaurant_name}
        className="absolute inset-0 h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
    ) : (
      <GradientArt palette={palette} />
    )}

</div>
<div className="px-6 pt-5">
  <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: palette.accent }}>
    Featured Menu Item
  </p>
  <h1 className="mt-2 font-display text-xl font-semibold leading-snug text-white">
    {data.restaurant_name}
  </h1>
  {ratingLabel ? (
    <p className="mt-2 text-xs font-semibold" style={{ color: palette.accent }}>
      {ratingLabel}
    </p>
  ) : null}
</div>

  <div className="p-6">
    <h2 className="font-display text-2xl font-bold text-white">
      {data.featured_item_name || "Featured Item"}
    </h2>
    {data.featured_item_description ? (
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">
        {data.featured_item_description}
      </p>
    ) : null}
    {(data.featured_action_link || data.menu_url) ? (
      <a
        href={externalUrl(data.featured_action_link || data.menu_url)}
        target="_blank"
        rel="noreferrer"
        className="mt-6 inline-flex min-h-[54px] w-full items-center justify-center rounded-2xl px-4 py-3 text-center text-base font-bold hover:opacity-90"
        style={{ background: palette.accent, color: "#000" }}
      >
        {data.featured_action_text || "View Menu"}
      </a>
    ) : (
      <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4 text-center text-sm text-zinc-400">
        Visit us to discover this featured item.
      </div>
    )}
    <CustomerActionGrid
      actions={automaticCustomerActions({
        websiteUrl: data.website_url,
        menuUrl: data.menu_url,
        phone: data.phone,
        address: data.address,
        excludeHref: data.featured_action_link || data.menu_url,
      })}
      onTrack={trackClick}
      className="mt-4"
    />
    {(data.address || data.hours) ? (
      <div className="mt-5 space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300">
        {data.address ? <p>📍 {data.address}</p> : null}
        {data.hours ? <p>🕒 {data.hours}</p> : null}
      </div>
    ) : null}
  </div>
</motion.div>
) : (

            <>
          {!claimed ? (
            <motion.div
              key="offer-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl shadow-2xl"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                {data.image_url && !imageFailed ? (
                  <img
                    src={data.image_url}
                    alt={data.restaurant_name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <GradientArt palette={palette} />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                <div className="absolute bottom-4 left-5 right-5">
                  <div
                    className="text-[10px] uppercase tracking-[0.2em] font-bold"
                    style={{ color: palette.accent }}
                  >
                    Instagram Exclusive
                  </div>

                  {ratingLabel ? (
                    <div
                      className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: palette.accent }}
                    >
                      {ratingLabel}
                    </div>
                  ) : null}

                  <h1 className="font-display font-semibold text-2xl sm:text-3xl leading-tight mt-1">
                    {data.restaurant_name}
                  </h1>
                  
                  {data?.description ? (
                   <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                    {data.description}
                   </p>
                 ) : null}
                </div>
              </div>

              <div className="p-6">
                <div className="label-caps !text-[9px]">This week’s offer</div>

                <div className="mt-2 font-display font-semibold text-xl leading-snug">
                  {data.offer}
                </div>

                <motion.button
                  onClick={onClaim}
                  disabled={claiming}
                  whileTap={{ scale: 0.98 }}
                  className="mt-6 w-full rounded-2xl py-4 font-semibold text-base transition-colors disabled:cursor-not-allowed disabled:opacity-80"
                  style={{
                    background: palette.accent,
                    color: "#000",
                  }}
                >
                  {claiming ? "Claiming…" : data.cta_text || "Claim Offer"}
                </motion.button>
                <CustomerActionGrid
                  actions={automaticCustomerActions({
                    websiteUrl: data.website_url,
                    menuUrl: data.menu_url,
                    phone: data.phone,
                    address: data.address,
                  })}
                  onTrack={trackClick}
                  className="mt-4"
                />

                {(data.address || data.hours) ? (
                  <div className="mt-4 space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                    {data.address ? (
                      <div className="text-zinc-300">
                        📍 {data.address}
                      </div>
                    ) : null}

                    {data.hours ? (
                      <div className="text-zinc-400">
                        🕒 {data.hours}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </motion.div>
          ) : (
            <CouponScreen
              palette={palette}
              restaurant={data.restaurant_name}
              offer={data.offer}
              offerTerms={data.offer_terms}
              code={claimCode}
              time={claimTime}
              instagram={data.instagram_handle}
              menuUrl={data.menu_url}
              websiteUrl={data.website_url}
              phone={data.phone}
              paymentLink={data.payment_link}
              showActivation={showActivation}
              imageUrl={data.image_url}
              imageFailed={imageFailed}
              setImageFailed={setImageFailed}
              onTrack={trackClick}
              ctaText={data.after_claim_button_text || data.cta_text}
              ctaLink={data.after_claim_button_link || data.cta_link}
              address={data.address}
              hours={data.hours}
            />
          )}

            </>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {data.instagram_handle ? (
              <a
                href={`https://instagram.com/${data.instagram_handle}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackClick("instagram_click")}
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/10 transition text-center"
              >
                Follow on Instagram
              </a>
            ) : null}

            <button
              onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
                toast.success("Link copied — share it anywhere.");
              }}
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/10 transition text-center"
            >
              Share this page
            </button>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-zinc-500">
        <div>
          Powered by{" "}
          <Link to="/" className="text-zinc-300 hover:text-[#00E5FF] transition">
            TapToTable
          </Link>{" "}
          · by Melted Port Studios
        </div>
        <div className="mt-2 flex items-center justify-center gap-3">
          <Link to="/privacy" className="text-zinc-400 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <span aria-hidden className="text-zinc-700">·</span>
          <Link to="/terms" className="text-zinc-400 hover:text-white transition-colors">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}

function CouponScreen({
  palette,
  restaurant,
  offer,
  offerTerms,
  code,
  time,
  instagram,
  menuUrl,
  websiteUrl,
  phone,
  paymentLink,
  showActivation,
  imageUrl,
  imageFailed,
  setImageFailed,
  onTrack,
  ctaText,
  ctaLink,
  address,
  hours,
}) {
  // After-claim action row:
  // 1 configurable button + automatic Website + Call + Directions.
  const stamp = time
    ? time.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  return (
    <motion.div
      key="coupon"
      initial={{ opacity: 0, scale: 0.96, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="mt-8 overflow-hidden rounded-3xl border bg-black/60 backdrop-blur-xl shadow-2xl"
      style={{ borderColor: `${palette.accent}55` }}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={restaurant}
            
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <GradientArt palette={palette} />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        <div className="absolute bottom-4 left-5 right-5">
          <div
            className="text-[10px] uppercase tracking-[0.2em] font-bold"
            style={{ color: palette.accent }}
          >
            Offer Claimed
          </div>

          <h1 className="font-display font-semibold text-2xl sm:text-3xl leading-tight mt-1">
            {restaurant}
          </h1>

          {instagram ? (
            <div className="mt-1 text-xs text-zinc-300">@{instagram}</div>
          ) : null}
        </div>
      </div>

      <div className="px-6 pt-7 pb-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
          className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
          style={{
            background: `${palette.accent}22`,
            border: `2px solid ${palette.accent}`,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <motion.path
              d="M5 13l4 4L19 7"
              stroke={palette.accent}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </svg>
        </motion.div>

        <div className="mt-5 font-display font-semibold text-xl sm:text-2xl leading-tight">
          Offer claimed
        </div>

        <div className="mt-2 text-sm text-zinc-400">
          Show this screen in-store to redeem your offer.
        </div>

        <div className="mt-3 text-xs text-zinc-500">
          Keep this page open or take a screenshot before visiting.
        </div>
      </div>

      <div
        aria-hidden
        className="relative mx-6"
        style={{
          height: "1px",
          backgroundImage: `linear-gradient(to right, ${palette.accent}55 50%, transparent 50%)`,
          backgroundSize: "10px 1px",
          backgroundRepeat: "repeat-x",
        }}
      />

      <div className="px-6 py-6">
        <div className="label-caps !text-[9px]" style={{ color: palette.accent }}>
          Your offer
        </div>

        <div className="mt-2 font-display font-semibold text-lg leading-snug">
          {offer}
        </div>

        <div className="mt-5 flex items-end justify-between gap-4">
          <div>
            <div className="label-caps !text-[9px]">Claim code</div>
            <div className="font-mono font-semibold tracking-[0.2em] text-lg mt-1">
              {code}
            </div>
          </div>

          <div className="text-right">
            <div className="label-caps !text-[9px]">Claimed</div>
            <div className="text-xs text-zinc-300 mt-1">{stamp}</div>
          </div>
        </div>

        {offerTerms ? (
          <div className="mt-6 text-center text-[11px] text-zinc-500">
            {offerTerms}
          </div>
        ) : null}

{address ? (
          <div className="mt-5 text-sm text-zinc-300">
            <div className="font-medium">
              📍 {address}
            </div>
          </div>
        ) : null}

        {hours ? (
          <div className="mt-3 text-sm text-zinc-400">
            🕒 {hours}
          </div>
        ) : null}

        {(ctaLink || menuUrl) ? (
          <div className="mt-6">
            <div className="mb-2 text-left text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Ready for the next step?
            </div>

            {ctaLink ? (
              <a
                href={externalUrl(ctaLink)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  const url = externalUrl(ctaLink);
                  if (url) {
                    window.open(url, "_blank", "noopener,noreferrer");
                  }
                  onTrack("custom_cta_click");
                }}
                className="inline-flex min-h-[56px] w-full items-center justify-center rounded-2xl px-5 py-4 text-center text-base font-bold transition-transform active:scale-[0.99]"
                style={{
                  background: palette.accent,
                  color: "#000",
                  boxShadow: `0 12px 28px ${palette.accent}33`,
                }}
              >
                {ctaText || "View Menu"}
              </a>
            ) : (
              <a
                href={menuUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => onTrack("menu_click")}
                className="inline-flex min-h-[56px] w-full items-center justify-center rounded-2xl px-5 py-4 text-center text-base font-bold transition-transform active:scale-[0.99]"
                style={{
                  background: palette.accent,
                  color: "#000",
                  boxShadow: `0 12px 28px ${palette.accent}33`,
                }}
              >
                View Menu
              </a>
            )}
          </div>
        ) : null}

        <CustomerActionGrid
          actions={automaticCustomerActions({
            websiteUrl,
            menuUrl,
            phone,
            address,
            excludeHref: ctaLink || (!ctaLink ? menuUrl : ""),
          })}
          onTrack={onTrack}
          className="mt-3"
        />

          {paymentLink && showActivation ? (
            <a
              href={paymentLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => onTrack("payment_click")}
              className="w-full inline-flex justify-center items-center gap-2 rounded-2xl py-3 font-semibold text-sm bg-[#00E5FF] text-black hover:bg-[#66FFFF] transition-colors"
            >
              Activate This Page
            </a>
          ) : null}
      </div>
    </motion.div>
  );
}

function GradientArt({ palette }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${palette.via} 0%, ${palette.from} 100%)`,
        }}
      />

      <div
        className="absolute -top-20 -right-20 h-64 w-64 rounded-full blur-3xl opacity-70"
        style={{ background: palette.accent }}
      />

      <div
        className="absolute -bottom-16 -left-16 h-52 w-52 rounded-full blur-3xl opacity-40"
        style={{ background: palette.accent }}
      />

      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="absolute inset-0 flex items-center justify-center">
        <svg width="72" height="72" viewBox="0 0 24 24" fill="none" className="opacity-80">
          <path
            d="M3 11h18M5 11V7a2 2 0 012-2h10a2 2 0 012 2v4M4 11v5a3 3 0 003 3h10a3 3 0 003-3v-5M8 19v2M16 19v2"
            stroke={palette.accent}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );
}
